import React from 'react';

/** @type {React.CSSProperties} */
const mapContainerStyle = {
	height: '18rem',
	overflow: 'visible',
	position: 'relative',
	width: '100%',
};

/** @type {React.CSSProperties} */
const mapFrameStyle = {
	backgroundColor: '#f7f9fc',
	border: '1px solid #d7dce5',
	borderRadius: '1rem',
	height: '18rem',
	overflow: 'hidden',
	position: 'relative',
	width: '100%',
};

/** @type {React.CSSProperties} */
const mapPanelStyle = {
	height: '18rem',
	position: 'relative',
	width: '100%',
};

/** @type {React.CSSProperties} */
const mapFallbackStyle = {
	alignItems: 'center',
	backgroundColor: '#f7f9fc',
	color: '#52637a',
	display: 'flex',
	fontSize: '0.95rem',
	height: '18rem',
	justifyContent: 'center',
	padding: '1rem',
	textAlign: 'center',
};

/**
 * @param {number} price
 * @returns {string}
 */
function formatMarkerPrice(price) {
	if (price === null || price === undefined || Number.isNaN(price)) {
		return '';
	}

	if (price >= 1000) {
		return `$${Math.round(price / 1000)}K`;
	}

	return `$${Math.round(price)}`;
}

/**
 * @returns {any}
 */
function createMarkerIcon() {
	const googleMaps = /** @type {any} */ (window).google?.maps;
	const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" width="56" height="72" viewBox="0 0 56 72">
			<defs>
				<filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
					<feDropShadow dx="0" dy="8" stdDeviation="8" flood-color="#10233c" flood-opacity="0.22"/>
				</filter>
			</defs>
			<g filter="url(#shadow)">
				<path fill="#dc2626" d="M28 4C17.507 4 9 12.507 9 23c0 15.266 17.094 31.907 18.003 32.782a1.5 1.5 0 0 0 1.994 0C29.906 54.907 47 38.266 47 23 47 12.507 38.493 4 28 4Z"/>
				<circle cx="28" cy="23" r="15" fill="#ffffff" fill-opacity="0.16"/>
			</g>
		</svg>
	`;

	return {
		anchor: new googleMaps.Point(28, 60),
		labelOrigin: new googleMaps.Point(28, 23),
		scaledSize: new googleMaps.Size(56, 72),
		url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
	};
}

/** @extends {React.Component<{ listings: any[]; onMarkerHover?: (address: string | null) => void }>} */
export default class ComparableMarketMap extends React.Component {
	/**
	 * @param {{ listings: any[]; onMarkerHover?: (address: string | null) => void; } | Readonly<{ listings: any[]; onMarkerHover?: (address: string | null) => void; }>} props
	 */
	constructor(props) {
		super(props);

		this.mapElementRef = React.createRef();
		this.map = null;
		/**
		 * @type {any[]}
		 */
		this.markers = [];
		this.initializationTimer = null;
	}

	componentDidMount() {
		this.initializeMapWhenReady();
	}

	/**
	 * @param {{ listings: any; }} prevProps
	 */
	componentDidUpdate(prevProps) {
		const prevListings = JSON.stringify(prevProps.listings);
		const currentListings = JSON.stringify(this.props.listings);

		if (prevListings !== currentListings) {
			this.initializeMapWhenReady();
		}
	}

	componentWillUnmount() {
		if (this.initializationTimer !== null) {
			window.clearTimeout(this.initializationTimer);
		}

		this.clearMarkers();

		if (this.props.onMarkerHover) {
			this.props.onMarkerHover(null);
		}
	}

	getValidListings() {
		return this.props.listings.filter(
			(listing) =>
				typeof listing.location?.latitude === 'number' &&
				typeof listing.location?.longitude === 'number'
		);
	}

	initializeMapWhenReady() {
		const googleMaps = /** @type {any} */ (window).google?.maps;
		const validListings = this.getValidListings();

		if (!validListings.length || !this.mapElementRef.current) {
			return;
		}

		if (!googleMaps) {
			this.initializationTimer = window.setTimeout(() => {
				this.initializeMapWhenReady();
			}, 300);

			return;
		}

		if (this.map === null) {
			this.map = new googleMaps.Map(this.mapElementRef.current, {
				clickableIcons: false,
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
			});
		}

		this.renderMarkers(validListings);
	}

	clearMarkers() {
		this.markers.forEach((marker) => {
			marker.setMap(null);
		});

		this.markers = [];
	}

	/**
	 * @param {any[]} listings
	 */
	renderMarkers(listings) {
		const googleMaps = /** @type {any} */ (window).google?.maps;

		if (this.map === null) {
			return;
		}

		this.clearMarkers();

		const bounds = new googleMaps.LatLngBounds();

		this.markers = listings.map((listing) => {
			const marker = new googleMaps.Marker({
				icon: createMarkerIcon(),
				label: {
					color: '#ffffff',
					fontSize: '13px',
					fontWeight: '700',
					text: formatMarkerPrice(listing.price),
				},
				map: this.map,
				position: {
					lat: listing.location.latitude,
					lng: listing.location.longitude,
				},
				title: listing.location.address,
			});

			marker.addListener('mouseover', () => {
				if (this.props.onMarkerHover) {
					this.props.onMarkerHover(listing.location.address);
				}
			});

			marker.addListener('mouseout', () => {
				if (this.props.onMarkerHover) {
					this.props.onMarkerHover(null);
				}
			});

			bounds.extend(marker.getPosition());

			return marker;
		});

		if (listings.length === 1) {
			this.map.setCenter(bounds.getCenter());
			this.map.setZoom(13);
			return;
		}

		this.map.fitBounds(bounds, 48);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		if (!this.getValidListings().length) {
			return (
				<div style={mapContainerStyle}>
					<div style={mapFallbackStyle}>
						{
							'Map pins will appear when property coordinates are available.'
						}
					</div>
				</div>
			);
		}

		return (
			<div style={mapContainerStyle}>
				<div style={mapFrameStyle}>
					<div ref={this.mapElementRef} style={mapPanelStyle} />
				</div>
			</div>
		);
	}
}
